import { JetsonHealthStatus } from '../constants';
import type {
  Exercise,
  Gym,
  Member,
  Trainer,
  Muscle,
  Station,
  TrainingRoutineSession,
  TrainingRoutineTemplate,
  GymProduct,
  PerformedRoutine,
  PerformedSetGroup,
  Manager,
  Program,
  SessionStatistic,
  Group,
  JetsonHealthDTO,
  HealthDetails,
  HealthDiskSpace,
  HealthPing,
  JetsonDiskSpaceDetails,
} from '../types';

export const isRecord = (obj: any): obj is Record<string, any> => {
  return Boolean(obj && typeof obj === 'object' && obj !== null);
};

const isOptionalString = (
  value: unknown,
): value is string | null | undefined => {
  return value === undefined || value === null || typeof value === 'string';
};

const isOptionalNumber = (value: unknown): value is number | undefined => {
  return value === undefined || typeof value === 'number';
};

const isOptionalBoolean = (value: unknown): value is boolean | undefined => {
  return value === undefined || typeof value === 'boolean';
};

const jetsonHealthStatuses = new Set<string>(Object.values(JetsonHealthStatus));

const isJetsonHealthStatusValue = (
  value: unknown,
): value is JetsonHealthStatus => {
  return typeof value === 'string' && jetsonHealthStatuses.has(value);
};

const isOptionalJetsonStatus = (
  value: unknown,
): value is JetsonHealthStatus | undefined => {
  return value === undefined || isJetsonHealthStatusValue(value);
};

const isJetsonDiskSpaceDetailsValue = (
  value: unknown,
): value is JetsonDiskSpaceDetails | undefined => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  const details = value as JetsonDiskSpaceDetails;
  return (
    isOptionalNumber(details.total) &&
    isOptionalNumber(details.free) &&
    isOptionalNumber(details.threshold) &&
    isOptionalBoolean(details.exists)
  );
};

const isHealthDiskSpaceValue = (
  value: unknown,
): value is HealthDiskSpace | undefined => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  const diskSpace = value as HealthDiskSpace;
  return (
    isOptionalJetsonStatus(diskSpace.status) &&
    isJetsonDiskSpaceDetailsValue(diskSpace.details)
  );
};

const isHealthPingValue = (value: unknown): value is HealthPing | undefined => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  const ping = value as HealthPing;
  return isOptionalJetsonStatus(ping.status);
};

const isHealthDetailsValue = (
  value: unknown,
): value is HealthDetails | undefined => {
  if (value === undefined || value === null) {
    return true;
  }
  if (!isRecord(value)) {
    return false;
  }
  const details = value as HealthDetails;
  return (
    isOptionalString(details.reason) &&
    isHealthDiskSpaceValue(details.diskSpace) &&
    isHealthPingValue(details.ping)
  );
};

export const isStation = (obj: any): obj is Station => {
  return Boolean(obj.id && obj.exerciseIds);
};

export const isTemplate = (obj: any): obj is TrainingRoutineTemplate => {
  return Boolean(obj.id && obj.routine?.templateInfo?.trainerId);
};

export const isProgram = (obj: any): obj is Program => {
  return Boolean(obj.id && Array.isArray(obj.programItems));
};

export const isExercise = (obj: any): obj is Exercise => {
  return Boolean(
    obj.id &&
      obj.name &&
      isOptionalString(obj.imageUrl) &&
      isOptionalString(obj.videoUrl) &&
      isOptionalBoolean(obj.isDeleted) &&
      isOptionalBoolean(obj.isPersonalized),
  );
};

export const isGym = (obj: any): obj is Gym => {
  return Boolean(
    obj.id &&
      obj.name &&
      isOptionalString(obj.logoUrl) &&
      isOptionalString(obj.blackLogoUrl),
  );
};

export const isMember = (obj: any): obj is Member => {
  return Boolean(obj.id && obj.pin);
};

export const isTrainer = (obj: any): obj is Trainer => {
  return Boolean(obj?.id && obj?.memberIds);
};

export const isManager = (obj: any): obj is Manager => {
  return Boolean(obj?.id && obj?.gymIds);
};

export const isGymProduct = (obj: any): obj is GymProduct => {
  return Boolean(obj.name);
};

export const isMuscle = (obj: any): obj is Muscle => {
  return Boolean(obj.id && obj.parentGroup);
};

export const isTrainingRoutineSession = (
  obj: any,
): obj is TrainingRoutineSession => {
  return Boolean(obj.id && obj.performedRoutine?.name);
};

export const isPerformedRoutine = (obj: any): obj is PerformedRoutine => {
  return Boolean(obj.performedExerciseGroups);
};

export const isPerformedSets = (obj: any): obj is PerformedSetGroup => {
  return Boolean(obj[0].status);
};

export const isSessionStatistic = (obj: any): obj is SessionStatistic => {
  return Boolean(obj.id);
};

export const isGroup = (obj: any): obj is Group => {
  return Boolean(obj.id);
};

export const isJetsonHealth = (obj: any): obj is JetsonHealthDTO => {
  if (!isRecord(obj)) {
    return false;
  }
  const candidate = obj as JetsonHealthDTO;
  if (!isJetsonHealthStatusValue(candidate.status)) {
    return false;
  }
  return (
    isOptionalString(candidate.rackNum) &&
    isOptionalString(candidate.gymId) &&
    isOptionalString(candidate.socId) &&
    isOptionalString(candidate.source) &&
    isOptionalString(candidate.reportedTime) &&
    isHealthDetailsValue(candidate.details)
  );
};

export const isRecordOfTrainingRoutineSessions = (
  obj: any,
): obj is Record<string, TrainingRoutineSession> => {
  return (
    isRecord(obj) &&
    Object.values(obj).every((value) => isTrainingRoutineSession(value))
  );
};
